import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const companyName = process.env.REACT_APP_COMPANY_NAME || 'Autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'legal@autopilot.co.zw';
const lastUpdated = '1 January 2025';

const sections = [
  {
    id: 'agreement',
    title: '1. Agreement to Terms',
    content: `These Terms of Service ("Terms") form a legally binding contract between you ("Operator", "you", "your") and ${companyName} ("${companyName}", "we", "us", "our") governing your access to and use of the ${companyName} platform, including all websites, APIs, bots, dashboards, and related services (collectively, the "Platform").

By creating an account, clicking "I agree", or using any part of the Platform, you confirm that:
(a) you have read and understood these Terms;
(b) you are at least 18 years old;
(c) you have the authority to bind yourself or the business entity you represent; and
(d) you agree to comply with these Terms and all applicable laws.

If you do not agree, you must not use the Platform.`,
  },
  {
    id: 'services',
    title: '2. Description of Services',
    content: `${companyName} provides a software platform that enables Operators to create, deploy, and manage automated chatbots on WhatsApp, Facebook Messenger, websites, and other supported channels.

Services include:
• A web-based dashboard for creating and managing bots;
• A visual Flow Builder for designing conversation flows;
• Integration with WhatsApp Business API via Meta Platforms, Inc.;
• Integration with Facebook Messenger via Meta Platforms, Inc.;
• Website chat widget generation;
• Voice bot services (where available);
• Payment processing facilitation;
• Analytics and reporting tools.

We reserve the right to modify, suspend, or discontinue any part of the Platform with reasonable notice. We will not be liable for any modification, suspension, or discontinuation.`,
  },
  {
    id: 'accounts',
    title: '3. Account Registration & Security',
    content: `**3.1 Accurate information**
You must provide accurate, current, and complete information when registering an account. You are responsible for keeping your account information up to date.

**3.2 Account security**
You are responsible for maintaining the confidentiality of your password and account credentials. You must notify us immediately at ${companyEmail} if you suspect unauthorised access to your account. We are not liable for any loss caused by unauthorised use of your account.

**3.3 One account per business**
Unless we expressly agree otherwise (e.g. Agency plan), accounts are for a single business entity. You may not share account access with users outside your organisation.

**3.4 Account verification**
We may require you to verify your identity or business details before activating certain features, particularly WhatsApp Business API access. You agree to cooperate with verification requests and provide truthful documentation.`,
  },
  {
    id: 'whatsapp-facebook-terms',
    title: '4. WhatsApp & Facebook — Special Terms',
    content: `**4.1 Compliance with Meta's policies**
Use of WhatsApp Business API and Facebook Messenger features is subject to Meta's Messaging Policy, WhatsApp Business Policy, Platform Terms, and Developer Policies (collectively "Meta's Policies"). You are solely responsible for reading, understanding, and complying with Meta's Policies. A breach of Meta's Policies may result in suspension of your WhatsApp or Facebook access, and may trigger suspension of your ${companyName} account.

**4.2 Business verification documents**
To register your own phone number with WhatsApp Business API, you must submit valid business verification documents including, but not limited to:
• Proof of business registration (e.g. certificate of incorporation, business licence, ZIMRA registration);
• A valid business email address matching your business domain;
• Physical business address.
You warrant that all documents and information submitted are genuine and accurate. Submitting false documents is grounds for immediate account termination and may be reported to relevant authorities.

**4.3 Facebook Page admin access**
When you use our "We set it up for you" service for Facebook bots, you authorise us to be added as a Page Manager or Page Developer on your specified Facebook Page. You acknowledge that:
(a) You retain ownership of and control over your Facebook Page at all times;
(b) You may remove our access at any time via Facebook Page Settings;
(c) We will request removal of our admin access once setup is complete;
(d) You are responsible for any actions taken on your Page by your own admin users.

**4.4 API credentials**
By providing WhatsApp Access Tokens or Facebook Page Access Tokens to the Platform, you grant ${companyName} a limited, revocable licence to use those credentials solely to operate your bots. You must provide System User tokens (not temporary personal tokens) where applicable. Expired tokens will cause your bot to stop functioning — you are responsible for keeping credentials valid.

**4.5 Message content**
You are solely responsible for the content of all messages sent by your bots. You must not use the Platform to send:
• Spam or unsolicited bulk messages;
• Content that violates Meta's Community Standards;
• Misleading or deceptive content;
• Content that infringes any intellectual property rights;
• Illegal content under Zimbabwean or applicable international law.

**4.6 End User consent**
You are responsible for obtaining all necessary consents from End Users before sending them messages via WhatsApp or Messenger. This includes compliance with opt-in requirements under Meta's Messaging Policy.`,
  },
  {
    id: 'plans-payment',
    title: '5. Plans, Payment & Invoices',
    content: `**5.1 Subscription plans**
The Platform is offered on a subscription basis. Plan details, features, and pricing are described on our Pricing page. We reserve the right to change pricing with 30 days' written notice.

**5.2 One-time setup fees**
Certain bot types (e.g. WhatsApp own-number registration, Facebook managed setup) attract a one-time setup fee payable before or upon service activation. Setup fees are non-refundable once work has commenced.

**5.3 Payment**
Payments are due in advance for the billing period. Accepted payment methods include Visa/Mastercard (via Stripe), EcoCash, OneMoney, and PayPal. You authorise us to charge your chosen payment method on each billing date.

**5.4 Failed payments**
If a payment fails, we will retry up to 3 times over 7 days and notify you by email. If payment is not received after 3 attempts, your account may be downgraded to the Free plan or suspended. Bots pending payment will not be activated.

**5.5 Invoices**
A PDF invoice is generated for every successful payment and available in your dashboard. Invoices for setup fees are created immediately upon bot creation and must be paid within 7 days.

**5.6 Refunds**
Monthly subscription fees are non-refundable once a billing period has commenced. We will consider refund requests for technical failures on our part on a case-by-case basis. Setup fees are non-refundable once setup work has commenced.

**5.7 Taxes**
You are responsible for any taxes applicable to your use of the Platform, including VAT or withholding taxes, unless otherwise stated.`,
  },
  {
    id: 'operator-obligations',
    title: '6. Operator Obligations',
    content: `As an Operator, you agree to:

**6.1 Lawful use**
Use the Platform only for lawful purposes and in accordance with these Terms, our Acceptable Use Policy, and all applicable laws.

**6.2 End User responsibility**
You are fully responsible for your interactions with End Users, the content of your bots, and compliance with all consumer protection, data protection, and messaging laws applicable to your business and jurisdiction.

**6.3 Data protection**
Where you collect personal data from End Users through bots, you act as the data controller. You must:
• Have a lawful basis for processing End User data;
• Maintain an appropriate privacy notice for your End Users;
• Comply with applicable data protection laws (including Zimbabwe's Cyber and Data Protection Act and, where applicable, the GDPR).

**6.4 Accurate bot content**
Ensure your bot provides accurate information. ${companyName} is not responsible for inaccurate, misleading, or harmful content in your bot flows.

**6.5 Notification of issues**
Promptly notify us at ${companyEmail} if you become aware of any security breach, unauthorised access, or misuse of your account or bots.`,
  },
  {
    id: 'ip',
    title: '7. Intellectual Property',
    content: `**7.1 Our IP**
The Platform, including all software, design, text, graphics, logos, and underlying code, is owned by ${companyName} and is protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works of any part of the Platform without our prior written consent.

**7.2 Your content**
You retain ownership of all content you create on the Platform, including bot flows, conversation scripts, and uploaded materials ("Your Content"). By using the Platform, you grant ${companyName} a limited, non-exclusive, royalty-free licence to host, store, display, and process Your Content solely to provide the Platform to you.

**7.3 Feedback**
If you provide feedback, suggestions, or ideas about the Platform, you grant us the right to use that feedback without restriction or compensation.`,
  },
  {
    id: 'liability',
    title: '8. Limitation of Liability',
    content: `**8.1 No warranties**
The Platform is provided "as is" and "as available" without any warranties of any kind, express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the Platform will be uninterrupted, error-free, or free of viruses.

**8.2 Limitation**
To the maximum extent permitted by law, ${companyName}'s total liability to you for any claim arising from or related to these Terms or the Platform — whether in contract, tort, or otherwise — shall not exceed the total fees paid by you to ${companyName} in the 3 months immediately preceding the event giving rise to the claim.

**8.3 Exclusion of consequential loss**
We shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, revenue, data, business, or goodwill, even if we were advised of the possibility of such damages.

**8.4 Third-party services**
We are not responsible for the availability, reliability, or content of Meta's WhatsApp or Facebook platforms, or any other third-party service we integrate with. Any downtime or policy change on Meta's platforms that affects your bots is outside our control.

**8.5 Force majeure**
We are not liable for delays or failures caused by events beyond our reasonable control, including natural disasters, government actions, internet outages, or changes to Meta's policies.`,
  },
  {
    id: 'termination',
    title: '9. Termination',
    content: `**9.1 By you**
You may cancel your subscription at any time from your dashboard. Cancellation takes effect at the end of your current billing period. You remain responsible for fees incurred up to that date.

**9.2 By us**
We may suspend or terminate your account immediately, without notice, if:
• You materially breach these Terms or our Acceptable Use Policy;
• You submit false documents during verification;
• Your account is used for spam, illegal activity, or in violation of Meta's Policies;
• We are required to do so by law or by Meta's instructions;
• You fail to pay fees after repeated reminders.

**9.3 Effect of termination**
Upon termination, your access to the Platform ceases. Your bot data and flows will be retained for 30 days, after which they will be deleted. You may request an export of your data within those 30 days.`,
  },
  {
    id: 'governing-law',
    title: '10. Governing Law & Disputes',
    content: `These Terms are governed by and construed in accordance with the laws of Zimbabwe.

In the event of a dispute, the parties agree to first attempt resolution through good-faith negotiation within 30 days of written notice of the dispute.

If a dispute cannot be resolved by negotiation, it shall be submitted to the jurisdiction of the courts of Zimbabwe.

Nothing in this clause prevents either party from seeking urgent injunctive relief in any court of competent jurisdiction.`,
  },
  {
    id: 'general',
    title: '11. General',
    content: `**Entire agreement**: These Terms, together with our Privacy Policy, Cookie Policy, and Acceptable Use Policy, constitute the entire agreement between you and ${companyName} regarding the Platform.

**Severability**: If any provision is found to be unenforceable, it will be modified to the minimum extent necessary, and the remaining provisions will continue in full force.

**No waiver**: Our failure to enforce any right or provision does not constitute a waiver of that right.

**Assignment**: You may not assign your rights or obligations under these Terms without our prior written consent. We may assign our rights to any affiliate or in connection with a business transfer.

**Notices**: Legal notices to us must be sent to ${companyEmail}. We will send notices to the email address associated with your account.

**Updates**: We may update these Terms. We will notify you of material changes by email and by posting the updated Terms on the Platform. Continued use after 30 days' notice constitutes acceptance.`,
  },
];

export default function TermsOfService() {
  const [activeSection, setActiveSection] = useState('agreement');

  const renderContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '14px', marginBottom: '8px', marginTop: i > 0 ? '20px' : 0 }}>{line.replace(/\*\*/g, '')}</p>;
      }
      if (line.includes('**')) {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        return (
          <p key={i} style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.8', marginBottom: '8px' }}>
            {parts.map((part, j) => j % 2 === 1 ? <strong key={j} style={{ color: '#e2e8f0' }}>{part}</strong> : part)}
          </p>
        );
      }
      if (line.startsWith('• ')) {
        return <p key={i} style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.8', marginBottom: '6px', paddingLeft: '16px', borderLeft: '2px solid #1e2330' }}>{line.slice(2)}</p>;
      }
      if (line.trim() === '') return <div key={i} style={{ height: '8px' }} />;
      return <p key={i} style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.8', marginBottom: '8px' }}>{line}</p>;
    });
  };

  return (
    <>
      <SEO title={`Terms of Service | ${companyName}`} description={`The legal terms governing use of the ${companyName} platform.`} keywords="terms of service, terms and conditions, legal" type="website" />

      <style>{`
        * { box-sizing: border-box; }
        .policy-nav-item { transition: all 0.15s; cursor: pointer; }
        .policy-nav-item:hover { background: rgba(91,79,232,0.1); }
        @media (max-width: 768px) { .policy-layout { flex-direction: column !important; } .policy-sidebar { width: 100% !important; position: static !important; border-right: none !important; border-bottom: 1px solid #1e2330 !important; } }
      `}</style>

      <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: '#0f1117', color: 'white', minHeight: '100vh' }}>
        <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: '60px', background: 'rgba(15,17,23,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1e2330', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px' }}>
          <Link to="/"><img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain' }} /></Link>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Link to="/privacy" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Privacy</Link>
            <Link to="/cookies" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Cookies</Link>
            <Link to="/acceptable-use" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Acceptable Use</Link>
          </div>
        </nav>

        <div style={{ paddingTop: '100px', paddingBottom: '40px', paddingLeft: '32px', paddingRight: '32px', borderBottom: '1px solid #1e2330' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ display: 'inline-block', background: 'rgba(91,79,232,0.15)', border: '1px solid rgba(91,79,232,0.3)', color: '#818cf8', fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', letterSpacing: '0.08em', marginBottom: '16px' }}>LEGAL</div>
            <h1 style={{ fontSize: '36px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '8px' }}>Terms of Service</h1>
            <p style={{ color: '#475569', fontSize: '14px' }}>Last updated: {lastUpdated}</p>
          </div>
        </div>

        <div className="policy-layout" style={{ display: 'flex', maxWidth: '1100px', margin: '0 auto', minHeight: 'calc(100vh - 200px)' }}>
          <div className="policy-sidebar" style={{ width: '260px', flexShrink: 0, borderRight: '1px solid #1e2330', padding: '24px 0', position: 'sticky', top: '60px', height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
            <div style={{ padding: '0 16px', marginBottom: '12px', fontSize: '10px', fontWeight: '700', color: '#334155', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Sections</div>
            {sections.map(s => (
              <div key={s.id} className={`policy-nav-item ${activeSection === s.id ? 'active' : ''}`} onClick={() => { setActiveSection(s.id); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                style={{ padding: '9px 20px', fontSize: '13px', color: activeSection === s.id ? '#a5b4fc' : '#64748b', borderLeft: activeSection === s.id ? '3px solid #5B4FE8' : '3px solid transparent', fontWeight: activeSection === s.id ? '600' : '400' }}>
                {s.title}
              </div>
            ))}
          </div>
          <div style={{ flex: 1, padding: '40px 48px', overflowY: 'auto' }}>
            {sections.map(s => (
              <div key={s.id} id={s.id} style={{ marginBottom: '48px', scrollMarginTop: '80px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#e2e8f0', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #1e2330' }}>{s.title}</h2>
                {renderContent(s.content)}
              </div>
            ))}
            <div style={{ marginTop: '48px', padding: '24px', background: '#15181f', border: '1px solid #1e2330', borderRadius: '14px', display: 'flex', gap: '16px' }}>
              <span style={{ fontSize: '24px' }}>📧</span>
              <div>
                <div style={{ color: '#e2e8f0', fontWeight: '600', marginBottom: '4px' }}>Legal enquiries</div>
                <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '10px' }}>Questions about these terms? Contact our legal team.</div>
                <a href={`mailto:${companyEmail}`} style={{ color: '#818cf8', fontSize: '13px', textDecoration: 'none' }}>{companyEmail}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
